// The decisions inside the shortlist's async ATS export — `onCandidatesExportRequest` /
// `exportCandidatesPollStatus` (`extension-alert-candidates.component.ng1.ts:1005-1186`) and their
// contacts twins (`extension-alert-client-contacts.component.ng1.ts:924-1040`) — kept out of
// `ExportToAtsModal` so they can be tested without a DOM or a clock.
//
// ⚠️ THE TWO PRODUCTS ARE ONE FLOW. The contacts controller is the candidates one with the noun
// changed: same window class, same copy table, same 1s poll against the SAME status endpoint, and
// a modal template that differs from the candidates one in indentation alone.
import type { ExportListTarget, ExportStatusResponse } from '@woo-ext/api'

/** `ATSType.BULLHORN` / `ATSType.JOBDIVA` as `careero.atsName` spells them (`shortlist.constants.ng1.ts`). */
const BULLHORN = 'Bullhorn'
const JOBDIVA = 'JobDiva'

/**
 * `exportGoToATSCopyOptions` + `exportGoToATSCopyDefault` — the label of the "Done!" button.
 *
 * ⚠️ JOBDIVA'S BUTTON SAYS "Close", AND THAT IS ALL IT DOES. Only Bullhorn's opens anything
 * (`openInBullhorn` tests `atsName == BULLHORN`), so on every other ATS "Go to list" closes the
 * dialogue and goes nowhere. Upstream's, kept: the label is a promise the button does not keep,
 * and fixing it is a redesign.
 */
export const EXPORT_GO_TO_ATS_COPY: Readonly<Record<string, string>> = {
  [BULLHORN]: 'View in Bullhorn',
  [JOBDIVA]: 'Close',
}

export const EXPORT_GO_TO_ATS_COPY_DEFAULT = 'Go to list'

export function exportGoToAtsCopy(atsName: string | undefined): string {
  if (atsName === undefined) return EXPORT_GO_TO_ATS_COPY_DEFAULT
  return EXPORT_GO_TO_ATS_COPY[atsName] ?? EXPORT_GO_TO_ATS_COPY_DEFAULT
}

/** `openInBullhorn()` sends `OpenHotlist` to the parent frame on Bullhorn and on nothing else. */
export function opensHotlistInAts(atsName: string | undefined): boolean {
  return atsName === BULLHORN
}

/** `this.$interval(…, 1000)`. */
export const EXPORT_POLL_INTERVAL_MS = 1000

/** `timePassed >= 7` — seconds, measured from just after the start call answered. */
export const EXPORT_SLOW_AFTER_MS = 7000

/**
 * `onExportCandidates`' request: the PLAIN Export creates a new private list named after the
 * hunter, with an empty description. The existing-list panel builds its own target
 * (`CandidateExportPanel`'s `ExportSubmitValues`) and reaches the same modal through
 * `OnCandidatesExport`; that is what `target` is for.
 */
export function plainExportTarget(alertName: string): ExportListTarget {
  return { name: alertName, description: '', isPublic: false }
}

/**
 * The modal's state: `isExportCandidatesInProgress` and `currentExportResponse`, plus whether the
 * poll is still running (`exportIntervalPromise` not yet cancelled).
 *
 * ⚠️ "IN PROGRESS" AND "POLLING" ARE DIFFERENT THINGS, and reading them as one is the easy
 * mistake. After 7s without success the spinner gives way to "taking longer than expected" — but
 * the interval is NOT cancelled: it goes on polling every second until the export succeeds or the
 * user presses Notify me. A late success still records the export.
 */
export interface ExportToAtsState {
  inProgress: boolean
  response: ExportStatusResponse | null
  polling: boolean
}

/** Set before the start call is even made: the modal opens on the spinner. */
export const INITIAL_EXPORT_TO_ATS_STATE: ExportToAtsState = { inProgress: true, response: null, polling: true }

/** Which of the template's three `ng-show` blocks is visible. */
export type ExportToAtsView = 'inProgress' | 'slow' | 'done'

export function exportToAtsView(state: ExportToAtsState): ExportToAtsView {
  if (state.inProgress) return 'inProgress'
  return state.response?.success === true ? 'done' : 'slow'
}

export interface ExportPollStep {
  state: ExportToAtsState
  /**
   * `cancelExportPollingAndCompleteDecisionData()` is due now: record the export and flag the rows.
   * True exactly once, on the poll that first reports success.
   */
  complete: boolean
}

/**
 * `exportCandidatesPollStatus` — one status answer, `elapsedMs` after the start call answered.
 *
 * ⚠️ SUCCESS STOPS THE POLL, THE 7s MARK DOES NOT. Both clear the spinner; only success cancels
 * the interval and completes. A response after the poll has stopped (a slow request still in
 * flight when Notify me was pressed) changes nothing.
 */
export function onExportStatus(
  state: ExportToAtsState,
  response: ExportStatusResponse,
  elapsedMs: number,
): ExportPollStep {
  if (!state.polling) return { state, complete: false }
  const success = response.success
  const inProgress = success || elapsedMs >= EXPORT_SLOW_AFTER_MS ? false : state.inProgress
  return {
    state: { inProgress, response, polling: !success },
    complete: success,
  }
}

/**
 * The start call failed. Upstream has no failure view: the rejection is unhandled, so the interval
 * is never set up and the dialogue spins on "Export in progress..." until its ✕ is pressed — the
 * generic request-failure alert is the only report. Kept: the spinner stays, and the poll that
 * never started is marked stopped so that a ✕ can let the dialogue go.
 */
export function onExportStartFailed(state: ExportToAtsState): ExportToAtsState {
  return { ...state, polling: false }
}

/**
 * `notifyMe()` — closes the dialogue, cancels the poll and completes AT ONCE, without waiting for
 * the export to succeed. Upstream's: the rows are flagged exported and `exportCandidates` is
 * recorded on the strength of the request having been accepted. Returns whether completion is due
 * (false if a success already completed it).
 */
export function onNotifyMe(state: ExportToAtsState): ExportPollStep {
  if (!state.polling) return { state, complete: false }
  return { state: { ...state, polling: false }, complete: true }
}

/**
 * `modal.closing`: backdrop click and Escape are PREVENTED — the export dialogue can only be left
 * by its buttons or the header's ✕. The ✕ (`dismiss('close')`) is allowed and does NOT stop the
 * poll; see `ExportToAtsModal`.
 */
export function exportModalMayClose(action: string): boolean {
  return action !== 'backdrop click' && action !== 'escape key press'
}

/**
 * The CSV dialogue's `modal.closing`: backdrop click and Escape flag the rows exported, as its own
 * ✕ (`closeModal()`) does. The UNIFIED header's ✕ is `dismiss('close')`, which neither handler
 * sees — so closing from there leaves the rows unflagged. Upstream's, kept.
 */
export function csvModalCloseMarksExported(action: string): boolean {
  return action === 'backdrop click' || action === 'escape key press'
}

/** `extension-alert-candidates-csv-export` failure, verbatim (`alerts.error(...)`). */
export const CSV_EXPORT_ERROR_MESSAGE =
  "Oops! Something didn't go as planned. \n" +
  "Please reach out to us at hello@woo.io, and we'll be happy to assist you"
